import type { SelectChangeEvent } from '@all-manager/design-system-v2';
import { useState } from 'react';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { useGetAllDocuments } from '../../api/useGetAllDocuments';
import { useGetDocumentTypes } from '../../api/useGetDocumentTypes';
import type { DocumentSortField, SortDirection } from '../../api/keys';
import { DOCUMENTS_PAGE_SIZE_OPTIONS } from '../../constants/pagination';

type SortOption = {
  value: string;
  label: string;
  field: DocumentSortField;
  direction: SortDirection;
};

export const SORT_OPTIONS: SortOption[] = [
  { value: 'created_at:desc', label: 'Plus récents', field: 'created_at', direction: 'desc' },
  { value: 'created_at:asc', label: 'Plus anciens', field: 'created_at', direction: 'asc' },
  { value: 'name:asc', label: 'Nom (A → Z)', field: 'name', direction: 'asc' },
  { value: 'name:desc', label: 'Nom (Z → A)', field: 'name', direction: 'desc' },
];

const DEFAULT_SORT = SORT_OPTIONS[0];
export const ALL_TYPES = 'all';

export default function useDocumentsListPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DOCUMENTS_PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
  const [sort, setSort] = useState(DEFAULT_SORT);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const { data: documentTypes } = useGetDocumentTypes();

  const { data, isLoading, isError } = useGetAllDocuments({
    search: debouncedSearch || undefined,
    type: typeFilter === ALL_TYPES ? null : typeFilter,
    sortField: sort.field,
    sortDirection: sort.direction,
    page,
    pageSize: rowsPerPage,
  });

  const count = data?.count ?? 0;
  const hasFilters = Boolean(debouncedSearch) || typeFilter !== ALL_TYPES;
  const selectedTypeName = documentTypes?.find((type) => type.id === typeFilter)?.name;

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(0);
  }

  function handleTypeChange(event: SelectChangeEvent<string>) {
    setTypeFilter(event.target.value);
    setPage(0);
  }

  function handleSortChange(event: SelectChangeEvent<string>) {
    setSort(SORT_OPTIONS.find((option) => option.value === event.target.value) ?? DEFAULT_SORT);
    setPage(0);
  }

  function handleRowsPerPageChange(value: number) {
    setRowsPerPage(value);
    setPage(0);
  }

  function handleResetFilters() {
    setSearch('');
    setTypeFilter(ALL_TYPES);
    setPage(0);
  }

  return {
    documents: data?.data ?? [],
    documentTypes: documentTypes ?? [],
    count,
    isLoading,
    isError,
    isLibraryEmpty: !isLoading && !isError && count === 0 && !hasFilters,
    hasNoResult: !isLoading && !isError && count === 0 && hasFilters,
    noResultSubtitle: getNoResultSubtitle(debouncedSearch, selectedTypeName),
    showPagination: count > DOCUMENTS_PAGE_SIZE_OPTIONS[0],
    page,
    rowsPerPage,
    search,
    typeFilter,
    sortValue: sort.value,
    handleSearchChange,
    handleTypeChange,
    handleSortChange,
    handlePageChange: setPage,
    handleRowsPerPageChange,
    handleResetFilters,
  };
}

function getNoResultSubtitle(search: string, typeName: string | undefined) {
  if (search && typeName) {
    return `Aucun document « ${typeName} » ne correspond à « ${search} ».`;
  }

  if (search) {
    return `Aucun document ne correspond à « ${search} ».`;
  }

  return `Aucun document de nature « ${typeName ?? 'sélectionnée'} ».`;
}
